import type { ManufacturingCase } from '@infinitform/platform-core';

/**
 * Pure set-add. Returns `prev` unchanged (by identity) when `id` is already
 * present, so signal `.update()` calls can skip re-triggering computeds.
 */
export function addDirty(prev: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (prev.has(id)) return prev;
  const next = new Set(prev);
  next.add(id);
  return next;
}

/**
 * Pure set-remove. Returns `prev` unchanged (by identity) when `id` isn't
 * present.
 */
export function removeDirty(prev: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!prev.has(id)) return prev;
  const next = new Set(prev);
  next.delete(id);
  return next;
}

/**
 * Pure set-remove for many ids at once. Returns `prev` unchanged (by
 * identity) when none of `ids` are present — used when removing a whole
 * case's worth of variants in one update.
 */
export function removeManyDirty(
  prev: ReadonlySet<string>,
  ids: readonly string[],
): ReadonlySet<string> {
  if (ids.length === 0) return prev;
  let changed = false;
  const next = new Set(prev);
  for (const id of ids) {
    if (next.delete(id)) changed = true;
  }
  return changed ? next : prev;
}

/** variantIds belonging to one method — the group `markCaseDirty` marks stale. */
export function variantIdsOfCase(
  cases: readonly Pick<ManufacturingCase, 'methodId' | 'variants'>[] | undefined,
  methodId: string | null | undefined,
): string[] {
  if (!methodId) return [];
  const target = cases?.find((c) => c.methodId === methodId);
  return target?.variants?.map((v) => v.variantId) ?? [];
}

/**
 * Read the staleness a project recorded at its last save.
 *
 * Deliberately forgiving: projects written before this field existed have no
 * `staleSinceRun` at all, and a hand-edited config could hold anything.
 * Anything unrecognised reads as "nothing is stale" — the same state a fresh
 * project starts in — rather than throwing during project load.
 */
export function readStaleSinceRun(project: unknown): {
  config: boolean;
  variantIds: ReadonlySet<string>;
} {
  const stale = (project as { staleSinceRun?: unknown } | null | undefined)?.staleSinceRun as
    | { config?: unknown; variantIds?: unknown }
    | null
    | undefined;

  if (!stale || typeof stale !== 'object') {
    return { config: false, variantIds: new Set() };
  }

  const ids = Array.isArray(stale.variantIds)
    ? stale.variantIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];

  return { config: stale.config === true, variantIds: new Set(ids) };
}
